import React, { useEffect, useMemo, useState } from 'react';
import { Button, Empty, Input, Segmented, Space, Table, Tag, message } from 'antd';
import type { TableColumnsType } from 'antd';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { ACCOUNTS_BY_IDS, DataPortalAccountType, SEARCH_ACCOUNTS } from '../graphql/queries';

interface Props {
    /** 已选人员的账号ID列表（受控） */
    value?: string[];
    onChange?: (value: string[]) => void;
}

/**
 * 可见范围人员选择器。
 *
 * 原方案要求管理员填账号ID或手机号，但管理员只知道姓名 —— 实际使用得先去人员管理页
 * 查 ID 再回来粘。这里改为按姓名查。
 *
 * 两条硬约束：
 * 1. **必须点「搜索」或按回车才发请求**，输入过程中不做联想 —— 姓名列上的索引挡不住
 *    每敲一个字母就查一次库（`account` 表含全部司机账号）。
 * 2. **姓名完全相等**才算命中，且只返回启用中的账号 —— 模糊匹配会把「Kevin Lee Jr」
 *    一起捞回来让配置人员误选。这两条都由后端强制，前端只是照同样的规则做交互。
 */
const AccountPicker: React.FC<Props> = ({ value = [], onChange }) => {
    const { t } = useTranslation();
    const client = useApolloClient();

    const [keyword, setKeyword] = useState('');
    const [searchBy, setSearchBy] = useState<'name' | 'phone'>('name');
    const [searching, setSearching] = useState(false);
    /** null = 还没搜过（不展示结果区）；[] = 搜过但无结果 */
    const [results, setResults] = useState<DataPortalAccountType[] | null>(null);
    /** 已选人员的姓名等信息，用来把「只有 ID」的存量配置显示成「姓名 · ID」 */
    const [selectedDetails, setSelectedDetails] = useState<Record<string, DataPortalAccountType>>({});

    const selectedIds = useMemo(() => value ?? [], [value]);

    // 打开弹窗时把已配置的 ID 批量回查姓名。含已停用账号：直接过滤掉会让那条配置
    // 变成看不见的幽灵数据，管理员无从移除
    useEffect(() => {
        const missing = selectedIds.filter((id) => !selectedDetails[id]);
        if (missing.length === 0) return;
        let cancelled = false;
        client
            .query<{ dataPortalAccountsByIds: DataPortalAccountType[] }>({
                query: ACCOUNTS_BY_IDS,
                variables: { ids: missing },
                fetchPolicy: 'network-only',
            })
            .then((res) => {
                if (cancelled) return;
                const loaded = res.data?.dataPortalAccountsByIds ?? [];
                setSelectedDetails((prev) => ({
                    ...prev,
                    ...Object.fromEntries(loaded.map((account) => [account.id, account])),
                }));
            })
            .catch((e) => console.warn('Fetch data portal accounts by ids failed', e));
        return () => {
            cancelled = true;
        };
    }, [selectedIds, selectedDetails, client]);

    const byPhone = searchBy === 'phone';

    const handleSearch = async () => {
        const kw = keyword.trim();
        if (!kw) {
            message.warning(
                (byPhone
                    ? t('central.dataPortalConfig.accountPicker.needPhone')
                    : t('central.dataPortalConfig.accountPicker.needFullName')) as string,
            );
            return;
        }
        // 手机号模式前端先挡非数字，避免一次必然失败的请求
        if (byPhone && !/^\d+$/.test(kw)) {
            message.warning(t('central.dataPortalConfig.accountPicker.phoneDigitsOnly') as string);
            return;
        }
        setSearching(true);
        try {
            const res = await client.query<{ dataPortalSearchAccounts: DataPortalAccountType[] }>({
                query: SEARCH_ACCOUNTS,
                variables: { keyword: kw, byPhone },
                fetchPolicy: 'network-only',
            });
            setResults(res.data?.dataPortalSearchAccounts ?? []);
        } catch (e: any) {
            message.error(e.message);
        } finally {
            setSearching(false);
        }
    };

    const pick = (account: DataPortalAccountType) => {
        if (selectedIds.includes(account.id)) {
            message.info(t('central.dataPortalConfig.accountPicker.alreadySelected') as string);
            return;
        }
        setSelectedDetails((prev) => ({ ...prev, [account.id]: account }));
        onChange?.([...selectedIds, account.id]);
    };

    const remove = (id: string) => onChange?.(selectedIds.filter((item) => item !== id));

    const columns: TableColumnsType<DataPortalAccountType> = [
        {
            title: t('central.dataPortalConfig.accountPicker.columnName'),
            dataIndex: 'name',
            key: 'name',
            render: (name: string, record) => (
                <Space size={4}>
                    <span>{name}</span>
                    {selectedIds.includes(record.id) && (
                        <Tag color="default">{t('central.dataPortalConfig.accountPicker.selectedTag')}</Tag>
                    )}
                </Space>
            ),
        },
        { title: t('central.dataPortalConfig.accountPicker.columnId'), dataIndex: 'id', key: 'id' },
        { title: t('central.dataPortalConfig.accountPicker.columnPhone'), dataIndex: 'phone', key: 'phone' },
        {
            title: t('central.dataPortalConfig.accountPicker.columnRole'),
            dataIndex: 'role',
            key: 'role',
            render: (role: string) => t(`enum.role.${role}`, { defaultValue: role }),
        },
    ];

    return (
        <div className="flex flex-col gap-2">
            {/* 姓名 / 手机号 切换：切换即清空关键词与上次结果，避免跨模式误用 */}
            <Segmented
                size="small"
                value={searchBy}
                onChange={(v) => {
                    setSearchBy(v as 'name' | 'phone');
                    setKeyword('');
                    setResults(null);
                }}
                options={[
                    { value: 'name', label: t('central.dataPortalConfig.accountPicker.byName') },
                    { value: 'phone', label: t('central.dataPortalConfig.accountPicker.byPhone') },
                ]}
            />
            <Space.Compact style={{ width: '100%' }}>
                <Input
                    value={keyword}
                    // 改了输入就收起上次结果：否则会让人以为下面那批结果对应的是新输入
                    onChange={(e) => {
                        setKeyword(e.target.value);
                        setResults(null);
                    }}
                    onPressEnter={handleSearch}
                    placeholder={(byPhone
                        ? t('central.dataPortalConfig.accountPicker.placeholderPhone')
                        : t('central.dataPortalConfig.accountPicker.placeholder')) as string}
                />
                <Button type="primary" loading={searching} onClick={handleSearch}>
                    {t('components.button.search')}
                </Button>
            </Space.Compact>

            {results != null && (
                <div className="border border-gray-200 rounded">
                    {results.length === 0 ? (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={t('central.dataPortalConfig.accountPicker.noMatch')}
                        />
                    ) : (
                        <Table
                            size="small"
                            rowKey="id"
                            columns={columns}
                            dataSource={results}
                            pagination={false}
                            scroll={{ y: 200 }}
                            onRow={(record) => ({
                                onClick: () => pick(record),
                                style: { cursor: 'pointer' },
                            })}
                        />
                    )}
                </div>
            )}

            {selectedIds.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {selectedIds.map((id) => {
                        const detail = selectedDetails[id];
                        const disabled = detail != null && !detail.enabled;
                        return (
                            <Tag key={id} closable onClose={() => remove(id)} color={disabled ? 'default' : 'blue'}>
                                {detail ? `${detail.name} · ${id}` : id}
                                {disabled ? `（${t('central.dataPortalConfig.accountPicker.disabledSuffix')}）` : ''}
                            </Tag>
                        );
                    })}
                </div>
            )}

            <div className="text-xs text-gray-400 leading-5">
                {t('central.dataPortalConfig.accountPicker.hint')}
            </div>
        </div>
    );
};

export default AccountPicker;
